// src/contexts/PinContext.jsx
// PATCH (this version):
//   FIX #11 (Issue 11): Lock type canonical value aligned.
//     Previously PinContext docs declared lockType as 'pin' | 'passphrase',
//     but Settings.js LOCK_TYPE_OPTIONS used { value: 'word' } for the
//     non-PIN option. This meant:
//       • changeLockType('word', ...)  →  stored 'word'  in localStorage
//       • PinContext initialised lockType to 'pin' (correct default)
//       • lockType read back as 'word' from localStorage
//       • PinContext docs said 'passphrase' but the actual value was 'word'
//     The app "accidentally worked" because all consumers only check
//     lockType === 'pin' (anything else is treated as passphrase). But the
//     inconsistency was a maintenance hazard.
//
//     Fix: Update docs and DEFAULT to use 'word' as the canonical non-PIN
//     value, matching LOCK_TYPE_OPTIONS in Settings.js. Additionally, add a
//     one-time migration in the init useEffect: if localStorage holds the old
//     value 'passphrase', rewrite it to 'word' so existing installs are
//     consistent after the update.
//
//   FIX #2 (Security): Remove MASTER_PIN / MASTER_PASSPHRASE override.
//     All VITE_* env vars are inlined into the JS bundle at build time —
//     any user who inspects the source can read the master credential and
//     call eraseLock() on any account. The override has been removed entirely.
//     PIN recovery should be done via Firebase Auth re-login → removePin()
//     in Settings. If a dev-only escape hatch is ever needed again, wrap it
//     in `if (import.meta.env.DEV)` so it is tree-shaken in production.
//
// ─────────────────────────────────────────────────────────────────
//  App lock context — PIN or passphrase protection.
//
//  Exports:
//    PinProvider         — wrap your app in this
//    usePin()            — returns:
//      hasPin            {boolean}   — a lock is set
//      isUnlocked        {boolean}   — session is unlocked
//      loading           {boolean}   — resolving initial state
//      activePin         {string}    — current PIN/passphrase (for crypto)
//      lockType          {'pin' | 'word'}   ← FIX #11: was 'pin' | 'passphrase'
//      setupPin          {(pin: string) => Promise<void>}
//      verifyPin         {(pin: string) => Promise<boolean>}
//      unlock            {() => void}
//      removePin         {() => void}
//      changePin         {(old, new, reEncryptFn?) => Promise<boolean>}
//      changeLockType    {(type: string, currentPin: string) => void}
//      lockOnClose       {boolean}
//      toggleLockOnClose {() => void}
//      suppressLock      {(ms: number) => void}
//
//  Storage:
//    ds_pin_hash         — SHA-256 hash of the PIN (never plain text)
//    ds_lock_type        — 'pin' | 'word'  (FIX #11: migrated from 'passphrase')
//    ds_lock_on_close    — '1' | '0'
// ─────────────────────────────────────────────────────────────────
import React, {
  createContext, useContext, useState, useEffect,
  useCallback, useRef,
} from 'react';

const PIN_HASH_KEY      = 'ds_pin_hash';
const LOCK_TYPE_KEY     = 'ds_lock_type';
const LOCK_ON_CLOSE_KEY = 'ds_lock_on_close';

// FIX #11: Canonical non-PIN lock type value.
// Settings.js LOCK_TYPE_OPTIONS uses 'word'; we align here.
const LOCK_TYPE_PIN  = 'pin';
const LOCK_TYPE_WORD = 'word';

// FIX #11: Migrate old 'passphrase' value to 'word' on read.
function normaliseLockType(stored) {
  if (stored === 'passphrase') return LOCK_TYPE_WORD; // migrate old installs
  if (stored === LOCK_TYPE_PIN)  return LOCK_TYPE_PIN;
  if (stored === LOCK_TYPE_WORD) return LOCK_TYPE_WORD;
  return LOCK_TYPE_PIN; // default
}

// FIX #2: MASTER_PIN / MASTER_PASSPHRASE removed.
// Vite inlines all VITE_* vars into the bundle — they are NOT secret.
// PIN recovery is handled via Firebase Auth re-login → removePin() in Settings.

const PinContext = createContext(null);

// ── SHA-256 via Web Crypto ────────────────────────────────────
async function sha256(str) {
  const encoded = new TextEncoder().encode(str);
  const buf     = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function PinProvider({ children }) {
  const [hasPin,      setHasPin]      = useState(false);
  const [isUnlocked,  setIsUnlocked]  = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [activePin,   setActivePin]   = useState('');
  const [lockType,    setLockType]    = useState(LOCK_TYPE_PIN);
  const [lockOnClose, setLockOnClose] = useState(false);

  const suppressRef = useRef(null);

  // ── Initialise from localStorage ──────────────────────────
  useEffect(() => {
    const stored      = localStorage.getItem(PIN_HASH_KEY);
    const rawType     = localStorage.getItem(LOCK_TYPE_KEY) || LOCK_TYPE_PIN;
    // FIX #11: normalise — migrate 'passphrase' → 'word' if found
    const storedType  = normaliseLockType(rawType);
    const storedClose = localStorage.getItem(LOCK_ON_CLOSE_KEY) === '1';

    // FIX #11: Write back the normalised value so old 'passphrase' installs
    // are permanently migrated after the first load.
    if (rawType !== storedType) {
      localStorage.setItem(LOCK_TYPE_KEY, storedType);
    }

    setHasPin(!!stored);
    setLockType(storedType);
    setLockOnClose(storedClose);

    if (!stored) setIsUnlocked(true);

    setLoading(false);
  }, []);

  // ── Auto-lock on visibility change ────────────────────────
  useEffect(() => {
    if (!lockOnClose || !hasPin) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (suppressRef.current && Date.now() < suppressRef.current) return;
        setIsUnlocked(false);
        setActivePin('');
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [lockOnClose, hasPin]);

  // ── Internal: erase stored lock entirely ─────────────────
  const eraseLock = useCallback(() => {
    localStorage.removeItem(PIN_HASH_KEY);
    setHasPin(false);
    setActivePin('');
  }, []);

  // ── Setup PIN ─────────────────────────────────────────────
  const setupPin = useCallback(async (pin) => {
    const hash = await sha256(pin);
    localStorage.setItem(PIN_HASH_KEY, hash);
    setHasPin(true);
    setIsUnlocked(true);
    setActivePin(pin);
  }, []);

  // ── Unlock (imperative, no verification) ─────────────────
  const unlock = useCallback(() => {
    setIsUnlocked(true);
  }, []);

  // ── Verify PIN ────────────────────────────────────────────
  // FIX #2: Master override removed. Only the real PIN hash is checked.
  const verifyPin = useCallback(async (pin) => {
    const stored = localStorage.getItem(PIN_HASH_KEY);
    if (!stored) return true;

    const hash = await sha256(pin);
    const ok   = hash === stored;
    if (ok) {
      setIsUnlocked(true);
      setActivePin(pin);
    }
    return ok;
  }, []);

  // ── Remove PIN ─────────────────────────────────────────────
  const removePin = useCallback(() => {
    eraseLock();
    setIsUnlocked(true);
  }, [eraseLock]);

  // ── Change PIN ─────────────────────────────────────────────
  const changePin = useCallback(async (oldPin, newPin, reEncryptFn) => {
    const stored = localStorage.getItem(PIN_HASH_KEY);
    if (!stored) return false;

    const oldHash = await sha256(oldPin);
    if (oldHash !== stored) return false;

    if (reEncryptFn) {
      await reEncryptFn(oldPin, newPin);
    }

    await setupPin(newPin);
    return true;
  }, [setupPin]);

  // ── Change lock type ──────────────────────────────────────
  // FIX #11: normaliseLockType applied to incoming type so any stray
  // 'passphrase' value from external callers is also normalised.
  const changeLockType = useCallback(async (type, currentPin) => {
    if (hasPin) {
      const stored = localStorage.getItem(PIN_HASH_KEY);
      if (stored) {
        const hash = await sha256(currentPin ?? '');
        if (hash !== stored) return;
      }
    }
    const normType = normaliseLockType(type);
    setLockType(normType);
    localStorage.setItem(LOCK_TYPE_KEY, normType);
  }, [hasPin]);

  // ── Toggle lock-on-close ──────────────────────────────────
  const toggleLockOnClose = useCallback(() => {
    setLockOnClose(prev => {
      const next = !prev;
      localStorage.setItem(LOCK_ON_CLOSE_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  // ── Suppress auto-lock temporarily ────────────────────────
  const suppressLock = useCallback((ms) => {
    suppressRef.current = Date.now() + ms;
  }, []);

  const value = {
    hasPin,
    isUnlocked,
    loading,
    activePin,
    lockType,
    setupPin,
    verifyPin,
    unlock,
    removePin,
    changePin,
    changeLockType,
    lockOnClose,
    toggleLockOnClose,
    suppressLock,
  };

  return (
    <PinContext.Provider value={value}>
      {children}
    </PinContext.Provider>
  );
}

export function usePin() {
  const ctx = useContext(PinContext);
  if (!ctx) throw new Error('usePin must be used inside <PinProvider>');
  return ctx;
}